import { useSelector } from 'react-redux';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { matchesDeploymentFilter } from '@/Components/Devices/inventoryFilters';
import { useApiQuery } from './useApiQuery';

// Fields a free-text inventory search is matched against; the API ANDs filters, so each is queried separately.
const SEARCH_FIELDS = ['deviceid', 'accountname'];
const FETCH_ALL_PAGE_SIZE = 500;

// Search, the Demo/Client `deployment` filter and the Staff device restriction cannot be expressed as
// server filters, so those queries fetch every match, filter, then count + slice to keep pages exact.
const useGetInventoryDevices = ({
  useruuid,
  queryParams,
  providerId,
  offset,
  limit,
  allowedDeviceUuids = null,
}) => {
  const trans = useSelector(translator);
  const deviceApi = useSelector((state) => state.apis.deviceApi);

  const query = new URLSearchParams(queryParams).toString();
  const allowedKey = allowedDeviceUuids ? [...allowedDeviceUuids].sort().join(',') : null;

  const fetchPage = (params, pageOffset, pageLimit) =>
    new Promise((resolve, reject) => {
      deviceApi.getDevicesByProviderId(
        providerId,
        pageOffset,
        pageLimit,
        params,
        apiCallback({
          translator: trans,
          successCallback: (data) => {
            resolve({
              devices: data?.devices ?? [],
              total: data?.page?.total_record_count ?? null,
            });
          },
          errorCallback: () => {
            reject([]);
          },
          failCallback: () => {
            reject([]);
          },
        })
      );
    });

  // Pages until the server's total is reached (or, if no total is reported, until a short page).
  const fetchAllMatches = async (params) => {
    const devices = [];
    for (;;) {
      const page = await fetchPage(params, devices.length, FETCH_ALL_PAGE_SIZE);
      devices.push(...page.devices);
      const done =
        page.devices.length === 0 ||
        (page.total != null
          ? devices.length >= page.total
          : page.devices.length < FETCH_ALL_PAGE_SIZE);
      if (done) return devices;
    }
  };

  const fetchDevices = async () => {
    const { search, deployment, ...baseParams } = queryParams;
    if (!search && !deployment && !allowedDeviceUuids) {
      return fetchPage(baseParams, offset, limit);
    }

    const results = search
      ? await Promise.all(
          SEARCH_FIELDS.map((field) =>
            fetchAllMatches({
              ...baseParams,
              filterkey: `${baseParams.filterkey},${field}`,
              filtervalue: `${baseParams.filtervalue},|${search}|`,
            })
          )
        )
      : [await fetchAllMatches(baseParams)];
    const allowed = allowedDeviceUuids && new Set(allowedDeviceUuids);
    const byUuid = new Map();
    results.flat().forEach((device) => {
      if (!matchesDeploymentFilter(device, deployment)) return;
      if (allowed && !allowed.has(device.deviceuuid)) return;
      byUuid.set(device.deviceuuid, device);
    });
    const matches = [...byUuid.values()].sort((a, b) =>
      (a.deviceid ?? '').localeCompare(b.deviceid ?? '')
    );
    return { devices: matches.slice(offset, offset + limit), total: matches.length };
  };

  return useApiQuery(
    ['inventoryDevices', useruuid, query, providerId, offset, limit, allowedKey],
    fetchDevices,
    {
      invalidateKeys: [['adminDashboardByDateTime']], // ✅ auto invalidate
    }
  );
};

export default useGetInventoryDevices;
